import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'react-tooltip';
import { kebabCase, upperFirst } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const tooltipDisplayPercent = (percent) => +Math.round(percent * 100);

export default function StatusBar({
  onTrack = 0,
  minorDisruption = 0,
  majorDisruption = 0,
  upcoming = 0,
  discontinued = 0,
  completed = 0,
  statusPending = 0,
  settings: {
    isA11yMode = false,
    onTrack: labelOnTrack = 'On Track',
    completed: labelCompleted = 'Completed',
    someDisruption: labelMinorDisruption = 'Minor Disruption',
    majorDisruption: labelMajorDisruption = 'Major Disruption',
    upcoming: labelUpcoming = 'Upcoming',
    statusPending: labelStatusPending = 'Status Pending',
    discontinued: labelDiscontinued = 'Discontinued',
  },
  keyPrefix,
  isCell = false,
  mobileLabel = null,
}) {
  if (!onTrack && !minorDisruption && !majorDisruption && !upcoming && !discontinued && !completed && !statusPending) {
    return null;
  }

  const activeSegments = [statusPending, onTrack, minorDisruption, majorDisruption, upcoming, discontinued, completed].filter(Boolean);

  let completedMessage;

  if (completed === 1) {
    completedMessage = (
      <span className="completed-thumb" role="status" aria-label={`Completed`}>
        <FontAwesomeIcon icon={['fal', 'thumbs-up']} />
        <span>Completed</span>
      </span>
    );
  } else {
    [{ onTrack }, { minorDisruption }, { majorDisruption }, { upcoming }, { discontinued }, { statusPending }].forEach((status) => {
      const stateValue = Object.values(status)[0];
      const stateLabel = Object.keys(status)[0];

      if (stateValue === 1) {
        completedMessage = (
          <span className="action-lv-label">
            <span className={kebabCase(stateLabel).toLowerCase()}>
              {
                {
                  labelOnTrack,
                  labelStatusPending,
                  labelMajorDisruption,
                  labelUpcoming,
                  labelDiscontinued,
                  labelMinorDisruption,
                }[`label${upperFirst(stateLabel)}`]
              }
            </span>
          </span>
        );
      }
    });
  }

  const showTooltip = !completedMessage;
  const statusSegment = (value, cssClass, label) => value ? (
    <span
      className={`status ${cssClass}`}
      style={{ width: `${value * 100}%` }}
      tabIndex={0}
      data-tooltip-id={`statusTip-${keyPrefix}`}
      data-tooltip-content={`${label} ${tooltipDisplayPercent(value)}%`}
      aria-label={`${label} ${tooltipDisplayPercent(value)}%`}
      {...(showTooltip ? { 'aria-describedby': `statusTip-${keyPrefix}` } : {})}
    />
  ) : null;

  return (
    <div
      className={`status-bar${completed === 1 ? ' completed' : ''}${isA11yMode ? ' a11y-mode' : ''}`}
      {...(isCell
        ? { role: 'cell' }
        : (isA11yMode && activeSegments.length > 1
          ? { role: 'group', 'aria-label': 'Status distribution' }
          : {})
      )}
    >
      {mobileLabel && <span className="mobile-col-label">{mobileLabel}</span>}
      <div className="child-bar">
        {completedMessage}
        {statusSegment(statusPending,   'status-pending',   labelStatusPending)}
        {statusSegment(onTrack,         'on-track',         labelOnTrack)}
        {statusSegment(minorDisruption, 'minor-disruption', labelMinorDisruption)}
        {statusSegment(majorDisruption, 'major-disruption', labelMajorDisruption)}
        {statusSegment(upcoming,        'upcoming',         labelUpcoming)}
        {statusSegment(discontinued,    'discontinued',     labelDiscontinued)}
        {statusSegment(completed,       'completed',        labelCompleted)}
      </div>
      {isA11yMode && activeSegments.length > 1 && (
        <ul className="status-bar-legend" role="list" aria-label="Status legend">
          {[
            { value: statusPending,   cssClass: 'status-pending',   label: labelStatusPending },
            { value: onTrack,         cssClass: 'on-track',         label: labelOnTrack },
            { value: minorDisruption, cssClass: 'minor-disruption', label: labelMinorDisruption },
            { value: majorDisruption, cssClass: 'major-disruption', label: labelMajorDisruption },
            { value: upcoming,        cssClass: 'upcoming',         label: labelUpcoming },
            { value: discontinued,    cssClass: 'discontinued',     label: labelDiscontinued },
            { value: completed,       cssClass: 'completed',        label: labelCompleted },
          ].filter(({ value }) => value > 0).map(({ value, cssClass, label }) => (
            <li key={cssClass} className="status-bar-legend-item">
              <span className={`status-bar-legend-dot ${cssClass}`} aria-hidden="true" />
              <span>{label} {tooltipDisplayPercent(value)}%</span>
            </li>
          ))}
        </ul>
      )}
      {!completedMessage ? (
        <Tooltip
          id={`statusTip-${keyPrefix}`}
          role="tooltip"
          openEvents={{ mouseenter: true, focus: true }}
          closeEvents={{ mouseleave: true, blur: true }}
        />
      ) : null}
    </div>
  );
}

StatusBar.propTypes = {
  onTrack: PropTypes.number,
  minorDisruption: PropTypes.number,
  majorDisruption: PropTypes.number,
  upcoming: PropTypes.number,
  discontinued: PropTypes.number,
  completed: PropTypes.number,
  statusPending: PropTypes.number,
  settings: PropTypes.shape({
    isA11yMode: PropTypes.bool,
    onTrack: PropTypes.string,
    completed: PropTypes.string,
    someDisruption: PropTypes.string,
    majorDisruption: PropTypes.string,
    upcoming: PropTypes.string,
    statusPending: PropTypes.string,
    discontinued: PropTypes.string,
  }).isRequired,
  keyPrefix: PropTypes.string.isRequired,
  isCell: PropTypes.bool,
  mobileLabel: PropTypes.string,
};
